import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { requiredValidation, emailValidation } from 'validators';
import { Button, PasswordField, TextField, Typography } from 'seagull';
import { routes } from "../../../../constants";
import { useIoContext, useStoreActions } from "../../../../contexts";
import { useEventReporter } from "../../../../event-reporter";
import { useUtmsParams } from "../../../../hooks/useUtmsParams";
import { handleRequest } from "../../../../infra/utils";
import { FormSeparatorHorizontal } from "../../components/FormSeparator";
import { useLocationCommonActions } from "../../../../providers/LocationProvider";
import { ErrorMessage, FooterLink, FormContainer, FormFields, VerticalFormFields, FormSubTitle, SubmitButtonField, LinkButton } from "../../components/FormComponents";
import sharedStyles from "../LoginScreen.module.scss";
import styles from "./Password.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const genericErrorMessage = 'Failed to login, please try again';
export default function PasswordStep(_ref) {
  let {
    email,
    onClickRequestOTP,
    titleMP,
    subtitleMP
  } = _ref;
  const [errorMessage, setErrorMessage] = useState();
  const {
    handleSubmit,
    control,
    formState,
    setFocus
  } = useForm({
    mode: 'onChange'
  });
  const {
    api,
    logBI
  } = useStoreActions();
  const [isLoading, setIsLoading] = useState(false);
  const {
    reportFormSubmission
  } = useEventReporter();
  const getUtms = useUtmsParams();
  const {
    getPageUrl
  } = useLocationCommonActions();
  const {
    locationReload
  } = useIoContext();
  async function onPasswordFormSubmit(_ref2) {
    let {
      email,
      password
    } = _ref2;
    logBI(10254, {});
    setErrorMessage(undefined);
    setIsLoading(true);
    const result = await handleRequest(api.auth.loginWithPassword({
      email,
      password,
      utms: getUtms(),
      anchorReferrer: getPageUrl()
    }));
    if (!result.success || !result.data.authToken) {
      var _result$error;
      if (((_result$error = result.error) === null || _result$error === void 0 ? void 0 : _result$error.body) === 'failed to login auth service') {
        setErrorMessage('Failed to login, please try again or reset your password');
      } else {
        setErrorMessage(genericErrorMessage);
      }
      setIsLoading(false);
    } else {
      reportFormSubmission('general-logged-in-successfully', null);
      locationReload();
    }
  }
  useEffect(() => {
    setFocus('password');
  }, []);
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsxs(FormContainer, {
      onSubmit: handleSubmit(onPasswordFormSubmit),
      "data-testid": "login-form",
      children: [titleMP, subtitleMP, /*#__PURE__*/_jsx(FormFields, {
        children: /*#__PURE__*/_jsxs(VerticalFormFields, {
          children: [/*#__PURE__*/_jsx(Controller, {
            control: control,
            name: "email",
            defaultValue: email,
            rules: emailValidation(undefined, {
              required: true
            }),
            render: _ref3 => {
              var _fieldState$error;
              let {
                field: {
                  onChange,
                  onBlur,
                  ref,
                  value
                },
                fieldState
              } = _ref3;
              return /*#__PURE__*/_jsx(TextField, {
                type: "email",
                label: "Work email",
                inputRef: ref,
                error: fieldState.isTouched && !!fieldState.error,
                helperText: fieldState.isTouched && ((_fieldState$error = fieldState.error) === null || _fieldState$error === void 0 ? void 0 : _fieldState$error.message),
                "data-testid": "email-input",
                value: value,
                onBlur: onBlur,
                onChange: onChange,
                placeholder: "Work email",
                disabled: isLoading,
                autoComplete: "username"
              });
            }
          }), /*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsx(Controller, {
              control: control,
              name: "password",
              defaultValue: "",
              rules: requiredValidation(true),
              render: _ref4 => {
                var _fieldState$error2;
                let {
                  field: {
                    onChange,
                    onBlur,
                    ref,
                    value
                  },
                  fieldState
                } = _ref4;
                return /*#__PURE__*/_jsx(PasswordField, {
                  label: "Password",
                  inputRef: ref,
                  value: value,
                  "data-testid": "password-input",
                  error: !!fieldState.error,
                  helperText: (_fieldState$error2 = fieldState.error) === null || _fieldState$error2 === void 0 ? void 0 : _fieldState$error2.message,
                  onBlur: onBlur,
                  onChange: onChange,
                  placeholder: "Password",
                  disabled: isLoading,
                  autoComplete: "current-password"
                });
              }
            }), /*#__PURE__*/_jsxs(Typography, {
              className: styles.forgotYourPassword,
              as: "p",
              variant: "paragraph2",
              skin: "secondary",
              children: ["Forgot your password?", ' ', /*#__PURE__*/_jsx(FooterLink, {
                toInternalLink: routes.resetPassword,
                "data-testid": "reset-password-link",
                children: "Reset it here"
              })]
            }), errorMessage && /*#__PURE__*/_jsx(ErrorMessage, {
              "data-testid": "err-message",
              children: errorMessage
            })]
          }), /*#__PURE__*/_jsx(SubmitButtonField, {
            disabled: !formState.isValid,
            isLoading: isLoading,
            "data-testid": "login-button",
            children: "Sign in"
          })]
        })
      })]
    }), /*#__PURE__*/_jsx(FormSeparatorHorizontal, {
      children: "Or"
    }), /*#__PURE__*/_jsxs("div", {
      className: styles.otpContainer,
      children: [/*#__PURE__*/_jsx(Button, {
        "data-testid": "request-otp-button",
        skin: "primaryOutline",
        onClick: () => onClickRequestOTP(),
        children: "Get a one-time code"
      }), /*#__PURE__*/_jsx(FormSubTitle, {
        children: "We'll email you a six digit code to sign in without a password."
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: sharedStyles.footer,
      children: /*#__PURE__*/_jsx(LinkButton, {
        toInternalLink: routes.login,
        "data-testid": "back-link",
        className: sharedStyles.backButton,
        children: "Back"
      })
    })]
  });
}